import React from 'react';
import { Link } from 'react-router-dom';

import PublicSiteShell from './PublicSiteShell.jsx';
import { appUrl } from '../../lib/hosts.js';

const metrics = [
  { label: '毛利边界', value: '清晰', tone: 'text-emerald-700' },
  { label: '退税影响', value: '可算', tone: 'text-amber-700' },
  { label: '审批记录', value: '留痕', tone: 'text-rose-700' }
];

const workflows = [
  ['客户', '客户分层、联系人、报价历史'],
  ['产品', 'BOM、工序、成本口径'],
  ['报价', '汇率、退税、费用、毛利'],
  ['交付', '审批、PDF、公开链接、留痕']
];

const capabilities = [
  {
    title: '让报价有依据',
    text: '把产品成本、汇率、退税、费用分摊和目标毛利放到同一套口径里，报价前先看清利润边界。'
  },
  {
    title: '让协作有秩序',
    text: '业务录入、财务复核、老板审批、客户交付各司其职，关键动作留下记录，不靠聊天记录追溯。'
  },
  {
    title: '让经营可复盘',
    text: '客户、产品、报价、审批和成交结果沉淀在系统里，企业可以回头看哪类订单值得接、哪类价格不能让。'
  }
];

const audiences = [
  ['制造型出口工厂', '产品多、成本细、报价频繁，需要把成本和报价放在同一张经营底稿里。'],
  ['工贸一体企业', '既要看工厂成本，也要看外贸成交，需要减少销售、财务、老板之间的反复确认。'],
  ['成长型外贸团队', '客户和产品逐步增加，需要把报价经验、利润底线和审批规则沉淀下来。']
];

function DashboardScene() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0 bg-[#f7f3ec]" />
      <div className="absolute left-[8%] top-[10%] h-[68%] w-[84%] rounded-[28px] border border-slate-200 bg-white shadow-2xl shadow-slate-300/60">
        <div className="flex h-12 items-center justify-between border-b border-slate-200 px-5">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-rose-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-emerald-500" />
          </div>
          <div className="h-2 w-40 rounded-full bg-slate-200" />
        </div>
        <div className="grid h-[calc(100%-3rem)] grid-cols-[220px_1fr]">
          <div className="border-r border-slate-200 bg-slate-50 px-5 py-6">
            <div className="mb-8 h-8 w-32 rounded bg-slate-900" />
            {['客户', '产品', '报价', '审批', '公开分享'].map((item, idx) => (
              <div key={item} className={[
                'mb-3 flex items-center gap-3 rounded-md px-3 py-2 text-sm',
                idx === 2 ? 'bg-chango-600 text-white' : 'text-slate-600'
              ].join(' ')}>
                <span className={idx === 2 ? 'h-2 w-2 rounded-full bg-white' : 'h-2 w-2 rounded-full bg-slate-300'} />
                {item}
              </div>
            ))}
          </div>
          <div className="px-7 py-6">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <div className="h-4 w-36 rounded bg-slate-200" />
                <div className="mt-3 h-8 w-64 rounded bg-slate-900" />
              </div>
              <div className="rounded-md bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">可提交审批</div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {metrics.map((m) => (
                <div key={m.label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs text-slate-500">{m.label}</div>
                  <div className={`mt-2 text-2xl font-semibold ${m.tone}`}>{m.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-[1.2fr_.8fr] gap-5">
              <div className="rounded-lg border border-slate-200 p-5">
                <div className="mb-4 h-3 w-28 rounded bg-slate-200" />
                <div className="space-y-3">
                  {[82, 64, 48, 71].map((w, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-20 text-xs text-slate-400">{['材料', '工序', '物流', '退税'][idx]}</div>
                      <div className="h-3 flex-1 rounded-full bg-slate-100">
                        <div className="h-3 rounded-full bg-chango-500" style={{ width: `${w}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-5">
                <div className="mb-4 h-3 w-24 rounded bg-slate-200" />
                <div className="grid grid-cols-4 gap-2">
                  {[42, 58, 78, 66, 52, 86, 74, 63].map((h, idx) => (
                    <div key={idx} className="flex h-28 items-end rounded bg-slate-50 px-1">
                      <div className="w-full rounded-t bg-amber-500" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(247,243,236,0.96)_0%,rgba(247,243,236,0.88)_36%,rgba(247,243,236,0.24)_100%)]" />
    </div>
  );
}

export default function Landing() {
  return (
    <PublicSiteShell>
      <section className="relative min-h-[76svh] overflow-hidden">
        <DashboardScene />
        <div className="relative z-10 mx-auto flex min-h-[76svh] max-w-6xl items-center px-6 py-16">
          <div className="max-w-2xl">
            <p className="mb-4 text-sm font-semibold text-chango-700">CHANGO EXPORT CLOUD</p>
            <h1 className="text-4xl font-semibold leading-tight text-slate-950 sm:text-6xl">
              长戈出口云
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-700">
              面向中国出口企业的经营数字化平台。长戈把客户、产品、成本、汇率、退税、报价、审批和交付连接成一条清晰的决策链，让业务敢报、财务能核、老板看得懂。
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={appUrl('/register')} className="rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                开通试用
              </a>
              <Link to="/product" className="rounded-md border border-slate-300 bg-white/80 px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-white">
                查看产品能力
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px bg-slate-200 px-6 sm:grid-cols-4">
          {workflows.map(([title, text]) => (
            <div key={title} className="bg-white py-8">
              <div className="text-lg font-semibold text-slate-950">{title}</div>
              <div className="mt-2 max-w-[12rem] text-sm leading-6 text-slate-600">{text}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#fbfaf7] py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-amber-700">从报价表升级成经营底座</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-950">长戈不替代企业已有财务系统，而是先把出口订单的利润判断做清楚。</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {capabilities.map((item) => (
              <article key={item.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
                <p className="mt-4 text-sm leading-7 text-slate-600">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-10 md:grid-cols-[.8fr_1.2fr] md:items-start">
            <div>
              <p className="text-sm font-semibold text-chango-700">适用企业</p>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">先服务出口经营里最容易失控的环节。</h2>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                出口企业的难点往往不在“有没有表格”，而在成本口径不一致、报价经验留不住、审批依据不完整。长戈先把这些关键环节做深。
              </p>
            </div>
            <div className="grid gap-4">
              {audiences.map(([title, text]) => (
                <article key={title} className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                  <h3 className="text-base font-semibold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-16 text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold text-amber-300">当前上线产品</p>
            <h2 className="mt-3 text-3xl font-semibold">长戈出口决策云，从第一张真实报价开始。</h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300">
              当前产品聚焦报价、成本、退税、审批和客户交付。企业可以先用一款产品建立清晰的出口经营规则，再按阶段扩展更多能力。
            </p>
          </div>
          <Link to="/product" className="inline-flex items-center justify-center rounded-md bg-white px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-slate-100">
            查看长戈产品体系
          </Link>
        </div>
      </section>
    </PublicSiteShell>
  );
}
